/*
 * cart-recommendations.js — ALT-CARTDRAWER-003
 *
 * Cart-aware "Complete the set" engine (progressive enhancement).
 *
 * The Liquid in `snippets/cart-drawer.liquid` server-renders ONE upsell card from
 * `settings.cart_drawer_upsell_collection` as the SSR fallback. With JS enabled this
 * engine swaps that card for a COMPLEMENT recommended from what is actually in the cart,
 * using Shopify's native Product Recommendations API (no app required):
 *
 *   {root}recommendations/products.json?product_id={id}&intent={complementary|related}&limit=4
 *
 * The API auto-excludes out-of-stock, $0, gift-card, and already-in-cart products.
 *
 * Algorithm:
 *   1. On drawer-open and on every cartUpdate, fetch {root}cart.js.
 *   2. Anchor = the line item with the highest final_line_price (the hero purchase).
 *   3. Fetch intent=complementary for the anchor. If empty → intent=related.
 *      If both empty → leave the SSR collection card untouched.
 *   4. Rebuild the upsell <li> with the first recommended product, matching the exact
 *      SSR markup/classes so styling + the shared <product-form> add flow are identical.
 *
 * No new libraries. Reuses Dawn's pubsub (PUB_SUB_EVENTS.cartUpdate) + <product-form>.
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var HOOK_SELECTOR = '[data-cart-recommendations]';
  var LIST_SELECTOR = '[data-cart-recommendations-list]';
  var DEBOUNCE_MS = 50;

  // Cache of the last recommendation keyed by anchor product id, so re-opening the drawer
  // with an unchanged cart does not re-hit the network. A cartUpdate forces a refetch.
  var cache = { anchorId: null, product: undefined };
  var latestRequestId = 0;
  var debounceTimer = null;
  var forceNext = false;
  var wasActive = false;

  /* ── Money formatting: mirrors the Liquid `money` filter via shop.money_format
     (passed in through data-money-format so the JS card matches the SSR card exactly). ── */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultOption(opt, def) {
      return opt == null ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value = '';
    var match = fmt.match(placeholderRegex);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function sizedImageUrl(url, width) {
    if (!url) return '';
    if (!width) return url;
    var clean = url.replace(/([?&])width=\d+/i, '$1').replace(/[?&]$/, '');
    var sep = clean.indexOf('?') === -1 ? '?' : '&';
    return clean + sep + 'width=' + width;
  }

  function getJSON(url) {
    return fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) throw new Error('Bad response ' + res.status);
      return res.json();
    });
  }

  function getCart() {
    return getJSON(ROOT + 'cart.js');
  }

  // Anchor = the line item with the highest final_line_price (the hero purchase).
  function pickAnchor(cart) {
    if (!cart || !cart.items || !cart.items.length) return null;
    return cart.items.reduce(function (best, item) {
      var price = typeof item.final_line_price === 'number' ? item.final_line_price : 0;
      var bestPrice = best ? best.final_line_price : -1;
      return price > bestPrice ? item : best;
    }, null);
  }

  function fetchRecs(productId, intent) {
    var url =
      ROOT +
      'recommendations/products.json?product_id=' +
      encodeURIComponent(productId) +
      '&intent=' +
      intent +
      '&limit=4';
    return getJSON(url)
      .then(function (data) {
        return (data && Array.isArray(data.products)) ? data.products : [];
      })
      .catch(function () {
        return [];
      });
  }

  // Complementary first (merchant-curated "pairs with"); fall back to related (auto).
  function resolveRecommendation(productId) {
    return fetchRecs(productId, 'complementary').then(function (products) {
      if (products.length) return products[0];
      return fetchRecs(productId, 'related').then(function (related) {
        return related.length ? related[0] : null;
      });
    });
  }

  function firstAvailableVariant(product) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available) return variants[i];
    }
    return variants[0] || null;
  }

  // Rebuild the upsell <li> to match the SSR markup in cart-drawer.liquid EXACTLY:
  // same .cart-drawer-upsell__* classes, aria-hidden media link, title, price, the
  // full-width labeled Add button inside a <product-form> so the shared add flow is
  // reused, and the same .product-form__error-message-wrapper so product-form.js can
  // surface a sold-out / quantity error on the JS-swapped card too (errorHtml is cloned
  // from the SSR [data-cart-recommendations-error] template, not hardcoded here).
  function buildItemHtml(product, hook, spinnerHtml, errorHtml) {
    var variant = firstAvailableVariant(product);
    if (!variant) return '';

    var moneyFormat = hook.getAttribute('data-money-format') || '';
    var imgWidth = parseInt(hook.getAttribute('data-image-width'), 10) || 224;
    var addToBagTpl = hook.getAttribute('data-label-add-to-bag') || 'Add [[title]] to bag';
    var addPriceTpl = hook.getAttribute('data-label-add-price') || 'Add · [[price]]';

    var title = product.title || '';
    var url = product.url || '#';
    var priceText = formatMoney(product.price, moneyFormat);

    // Locale-driven strings (patterns rendered server-side, tokens filled here).
    var ariaLabel = addToBagTpl.replace('[[title]]', title);
    var addLabel = addPriceTpl.replace('[[price]]', priceText);
    // Multi-variant products (e.g. a shoe with Talla/Color) must NOT quick-add a default
    // variant — they open the shared in-cart quick-view modal (#CartQuickView) so the
    // customer picks options WITHOUT leaving the cart. Single-variant → direct add.
    var multiVariant = ((product.variants || []).length > 1);

    var mediaInner = '';
    if (product.featured_image) {
      mediaInner =
        '<img' +
        ' src="' + escapeHtml(sizedImageUrl(product.featured_image, imgWidth)) + '"' +
        ' alt=""' +
        ' width="112"' +
        ' height="140"' +
        ' loading="lazy">';
    }

    var formId = 'CartUpsell-' + product.id;

    // multiVariant → Dawn's <modal-opener> wrapping a <button data-product-url> that opens
    // the shared #CartQuickView modal (quick-add.js fetches this product's <product-info>
    // into it). Same "Add · $price" label + loading spinner as the single-variant Add, so
    // the upsell CTA reads consistently. The spinner is required — QuickAddModal.show()
    // toggles opener .loading__spinner while it fetches the product HTML.
    var actionHtml = multiVariant
      ? '<modal-opener data-modal="#CartQuickView" class="cart-drawer-upsell__opener">' +
          '<button type="button" class="cart-drawer-upsell__add" aria-haspopup="dialog"' +
            ' data-product-url="' + escapeHtml(url) + '"' +
            ' aria-label="' + escapeHtml(ariaLabel) + '">' +
            '<span class="cart-drawer-upsell__add-label">' + escapeHtml(addLabel) + '</span>' +
            spinnerHtml +
          '</button>' +
        '</modal-opener>'
      : '<product-form class="cart-drawer-upsell__form" data-section-id="cart-drawer">' +
          '<form method="post" action="' + escapeHtml(ROOT + 'cart/add') + '" id="' + escapeHtml(formId) + '"' +
            ' accept-charset="UTF-8" class="form" novalidate="novalidate" data-type="add-to-cart-form">' +
            '<input type="hidden" name="form_type" value="product">' +
            '<input type="hidden" name="utf8" value="✓">' +
            '<input type="hidden" name="id" value="' + escapeHtml(variant.id) + '">' +
            '<button type="submit" name="add" class="cart-drawer-upsell__add"' +
              ' aria-label="' + escapeHtml(ariaLabel) + '">' +
              '<span class="cart-drawer-upsell__add-label">' + escapeHtml(addLabel) + '</span>' +
              spinnerHtml +
            '</button>' +
            (errorHtml || '') +
          '</form>' +
        '</product-form>';

    return (
      '<li class="cart-drawer-upsell__item" data-cart-recommendation>' +
        '<a href="' + escapeHtml(url) + '" class="cart-drawer-upsell__media" tabindex="-1" aria-hidden="true">' +
          mediaInner +
        '</a>' +
        '<div class="cart-drawer-upsell__body">' +
          '<div class="cart-drawer-upsell__info">' +
            '<a href="' + escapeHtml(url) + '" class="cart-drawer-upsell__title">' + escapeHtml(title) + '</a>' +
            '<span class="cart-drawer-upsell__price">' + escapeHtml(priceText) + '</span>' +
          '</div>' +
          actionHtml +
        '</div>' +
      '</li>'
    );
  }

  function render(force) {
    var requestId = ++latestRequestId;

    return getCart()
      .then(function (cart) {
        var anchor = pickAnchor(cart);
        if (!anchor) return; // empty cart → SSR/none, nothing to enhance

        // Respect the merchant config: only enhance a card the SSR actually rendered.
        var hook = document.querySelector(HOOK_SELECTOR);
        if (!hook || !hook.querySelector(LIST_SELECTOR)) return;

        var anchorId = anchor.product_id;

        var recPromise;
        if (!force && cache.anchorId === anchorId && cache.product !== undefined) {
          recPromise = Promise.resolve(cache.product);
        } else {
          recPromise = resolveRecommendation(anchorId).then(function (product) {
            cache = { anchorId: anchorId, product: product };
            return product;
          });
        }

        return recPromise.then(function (product) {
          if (requestId !== latestRequestId) return; // superseded by a newer trigger
          if (!product) return; // no complement/related → leave the SSR fallback in place

          // Re-query: the drawer section may have been re-rendered during the awaits.
          var freshHook = document.querySelector(HOOK_SELECTOR);
          if (!freshHook) return;
          var freshList = freshHook.querySelector(LIST_SELECTOR);
          if (!freshList) return;

          var spinnerTpl = freshHook.querySelector('[data-cart-recommendations-spinner]');
          var spinnerHtml = spinnerTpl ? spinnerTpl.innerHTML : '';

          // Same Dawn error-region markup the SSR card carries, cloned so the JS-swapped
          // card also has a .product-form__error-message-wrapper for product-form.js.
          var errorTpl = freshHook.querySelector('[data-cart-recommendations-error]');
          var errorHtml = errorTpl ? errorTpl.innerHTML : '';

          var html = buildItemHtml(product, freshHook, spinnerHtml, errorHtml);
          if (html) freshList.innerHTML = html; // upgrades <product-form>, wiring the add flow
        });
      })
      .catch(function () {
        /* Network/parse failure → silently keep the SSR fallback card. */
      });
  }

  function scheduleRender(force) {
    if (force) forceNext = true;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var f = forceNext;
      forceNext = false;
      debounceTimer = null;
      render(f);
    }, DEBOUNCE_MS);
  }

  // Progressive-enhancement fallback for the multi-variant quick-view opener. Dawn's
  // ModalOpener needs a <button>, and a <button> can't natively navigate — so if the
  // quick-view modal machinery never registered (quick-add.js failed to load/parse),
  // the opener would be a dead control. In that case ONLY, wire a delegated click that
  // sends the customer to the opener's data-product-url (the PDP). No-op on the normal
  // path (modal available), so Dawn's opener → modal.show flow is left untouched. The
  // card title + image <a href> remain a final safety net if even this script fails.
  function wireQuickViewFallback() {
    if (typeof customElements === 'undefined' || customElements.get('quick-add-modal')) return;
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var opener = target.closest('[data-modal="#CartQuickView"]');
      if (!opener) return;
      var btn = target.closest('[data-product-url]');
      if (!btn || !opener.contains(btn)) return;
      var url = btn.getAttribute('data-product-url');
      if (!url) return;
      e.preventDefault();
      window.location.href = url;
    });
  }

  function init() {
    wireQuickViewFallback();

    var drawer = document.querySelector('cart-drawer');

    // Hook 1 — drawer open: watch the `active` class the drawer toggles on open/close.
    if (drawer) {
      wasActive = drawer.classList.contains('active');
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName !== 'class') continue;
          var isActive = drawer.classList.contains('active');
          if (isActive && !wasActive) scheduleRender(false);
          wasActive = isActive;
        }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

      if (wasActive) scheduleRender(false); // drawer already open on load (edge case)
    }

    // Hook 2 — cart mutations: Dawn publishes cartUpdate after add/remove/qty change.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        scheduleRender(true); // force: cart changed, recs (and in-cart exclusions) are stale
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
